"use client";

import { useEffect, useState } from "react";
import { UserMenuButton } from "./UserMenuButton";

type HeaderUserInfo = { firstName: string | null; lastName: string | null; email: string; role: "patient" | "pro" | "admin" };

type Props = {
  variant: "header" | "pro";
  /** Rendu tant que l'état de session n'est pas connu, et si l'utilisateur est anonyme. */
  anonymous: React.ReactNode;
};

/**
 * Le Header est un Server Component rendu sur TOUTES les pages publiques,
 * lesquelles sont mises en cache CDN (Cloudflare). Server-render le menu
 * utilisateur avec le nom/email réel ferait fuiter ces données vers tout
 * visiteur qui reçoit la page depuis le cache (incident 2026-07-20).
 *
 * Donc : le SSR affiche toujours `anonymous` (safe à cacher), et ce composant
 * client fetch /api/auth/header après montage pour swapper vers le vrai menu
 * si une session existe. Léger flash anonyme (~100-300ms) pour les users
 * connectés — compromis standard pour ce pattern (cache public + header perso).
 */
export function AuthAwareUserMenu({ variant, anonymous }: Props) {
  const [user, setUser] = useState<HeaderUserInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/header", { credentials: "include", cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { user: null }))
      .then((d: { user: HeaderUserInfo | null }) => {
        if (!cancelled) setUser(d.user ?? null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return <>{anonymous}</>;

  return (
    <UserMenuButton
      firstName={user.firstName}
      lastName={user.lastName}
      email={user.email}
      role={user.role}
      variant={variant}
    />
  );
}
